import React, { useState } from 'react';
import { getUniversalEmbedUrl, getThumbnailUrl, formatHighlightType } from './helpers';

/**
 * Card showing a single highlight clip.
 *
 * Renders a static thumbnail until the user clicks play, then swaps in a
 * lazy-loaded iframe with autoplay. Avoiding immediate iframe creation
 * prevents browser crashes ("a problem occurred repeatedly") when many
 * highlights are listed on one page.
 *
 * @param {object} props
 * @param {object} props.highlight - Highlight record from the API.
 * @param {(matchId: string) => void} [props.onClick] - Called when the card
 *   body (not the video area) is clicked, e.g. to open the match.
 * @returns {JSX.Element}
 */
const HighlightCard = ({ highlight, onClick }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const isPending = highlight.render_status !== 'completed' || !highlight.video_url;
  const thumbnailUrl = getThumbnailUrl(highlight.video_url);
  const embedUrl = getUniversalEmbedUrl(highlight.video_url, { autoplay: true });

  const handleCardClick = () => {
    if (onClick) onClick(highlight.match_id);
  };

  const handlePlay = (event) => {
    event.stopPropagation();
    setIsPlaying(true);
  };

  return (
    <div className={`highlight-card ${isPending ? 'pending' : ''}`} onClick={handleCardClick}>
      <div className="highlight-card-video">
        {isPending ? (
          <div className="highlight-card-pending">
            <div className="pending-icon">⏳</div>
            <span>Rendering...</span>
          </div>
        ) : isPlaying && embedUrl ? (
          <iframe
            src={embedUrl}
            title={`${highlight.highlight_type} by ${highlight.player_name}`}
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            loading="lazy"
          />
        ) : embedUrl ? (
          <button
            type="button"
            className="highlight-card-thumbnail"
            onClick={handlePlay}
            aria-label={`Play ${highlight.highlight_type} by ${highlight.player_name}`}
          >
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                className="highlight-card-thumbnail-img"
              />
            ) : (
              <div className="highlight-card-thumbnail-fallback"><span>🎬</span></div>
            )}
            <span className="highlight-card-play-overlay" aria-hidden="true">
              <span className="highlight-card-play-icon">▶</span>
            </span>
          </button>
        ) : (
          <div className="highlight-card-no-video"><span>🎬</span></div>
        )}
      </div>
      <div className="highlight-card-info">
        <div className="highlight-card-header">
          <span className="highlight-card-badge">{formatHighlightType(highlight.highlight_type)}</span>
          <span className="highlight-card-round">Round {highlight.round_number}</span>
        </div>
        <div className="highlight-card-player">
          <span className="highlight-card-player-name">{highlight.player_name}</span>
          <span className="highlight-card-kills">{highlight.kills} kills</span>
        </div>
        {highlight.ai_comment && (
          <p className="highlight-card-comment">{highlight.ai_comment}</p>
        )}
        {highlight.team1_name && highlight.team2_name && (
          <div className="highlight-card-match">{highlight.team1_name} vs {highlight.team2_name}</div>
        )}
        <div className="highlight-card-date">
          {highlight.match_start_time
            ? new Date(highlight.match_start_time).toLocaleDateString('sv-SE')
            : highlight.detected_at
              ? new Date(highlight.detected_at).toLocaleDateString('sv-SE')
              : ''}
        </div>
      </div>
    </div>
  );
};

export default HighlightCard;
